import { 
  collection,
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  Timestamp,
  getDocs,
  limit,
  QueryDocumentSnapshot,
  DocumentData,
  runTransaction,
  doc,
  increment     
} from 'firebase/firestore';
import { db } from '../firebase';
import type { Message } from '../types/decision';
import Analytics from '../services/analytics'; 

// In messages.ts
export const saveMessage = async ({
  decisionId,
  participantId,
  content,
  sender = 'user' as const
}: {
  decisionId: string;
  participantId: string;
  content: string;
  sender?: 'user' | 'model';  
}): Promise<Message> => {
  try {
    // Start a transaction to handle the message save and potential participant count update
    return await runTransaction(db, async (transaction) => {
      // First check participant's current status
      const participantRef = doc(db, `decisions/${decisionId}/participants/${participantId}`);
      const participantSnap = await transaction.get(participantRef);
      
      if (!participantSnap.exists()) {
        throw new Error('Participant not found');
      }

      const participantData = participantSnap.data();
      const isPending = participantData.status === 'pending' && sender === 'user';

      // If transitioning from pending to active, update participant count
      if (isPending) {
        const decisionRef = doc(db, 'decisions', decisionId);
        transaction.update(decisionRef, {
          participants: increment(1),
          lastUpdated: Timestamp.now()
        });

        transaction.update(participantRef, {
          status: 'active' as const,
          lastActive: Timestamp.now()
        });
      }

      // Save the message
      const messageData = {
        decisionId,
        participantId,
        content,
        sender,
        timestamp: Timestamp.now()
      };

      const messageRef = doc(collection(db, `decisions/${decisionId}/messages`));
      transaction.set(messageRef, messageData);

      const message = {
        id: messageRef.id,
        ...messageData
      };
      
      // Track message sent event only for user messages
      if (sender === 'user') {
        Analytics.trackMessageSent(decisionId, participantId, content.length);
      }
      
      return message;
    });

  } catch (error) {
    console.error('Error saving message:', error);
    throw new Error('Failed to save message');
  }
};

// In services/messages.ts
export const subscribeToMessages = (
  decisionId: string,
  participantId: string,
  onUpdate: (messages: Message[]) => void,
  onError: (error: Error) => void
) => {
  const messagesQuery = query(
    collection(db, `decisions/${decisionId}/messages`),
    where('participantId', 'in', [participantId, 'ai']),
    orderBy('timestamp', 'asc')
  );

  return onSnapshot(
    messagesQuery,
    {
      next: (snapshot) => {
        // Get all messages in one go instead of accumulating
        const messages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Message[];
        
        onUpdate(messages);
      },
      error: (error) => {
        console.error('Error subscribing to messages:', error);
        onError(error);
      }
    }
  );
};

export const getMessageHistory = async (
  decisionId: string,
  participantId: string,
  maxMessages = 10
): Promise<Array<{role: 'user' | 'model', content: string}>> => {
  try {
    const messagesQuery = query(
      collection(db, `decisions/${decisionId}/messages`),
      where('participantId', '==', participantId),  // Only get this participant's messages
      orderBy('timestamp', 'desc'),
      limit(maxMessages)
    );

    const snapshot = await getDocs(messagesQuery);
    
    return snapshot.docs
      .map((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        return {
          role: data.sender as 'user' | 'model',
          content: data.content
        };
      })
      .reverse();
  } catch (error) {
    console.error('Error getting message history:', error);
    return Promise.reject(new Error('Failed to get message history'));
  }
};